import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TextField, TextareaField, SwitchField } from "@/components/kit/fields";
import { AssetUpload } from "@/components/branding/AssetUpload";
import { LandingView } from "@/components/branding/LandingView";
import { toast } from "sonner";
import { Palette, Eye } from "lucide-react";

function Group({ title, desc, children }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-6 space-y-4">
      <div><h3 className="text-sm font-medium text-foreground">{title}</h3>{desc && <p className="text-xs text-muted-foreground">{desc}</p>}</div>
      {children}
    </div>
  );
}

export function Branding() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["branding-admin"], queryFn: async () => (await http.get("/branding")).data, staleTime: 0 });
  const { data: plansData } = useQuery({ queryKey: ["public-plans"], queryFn: async () => (await http.get("/public/plans")).data, staleTime: 0 });
  const [b, setB] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (data) setB(data); }, [data]);

  const set = (path, value) => setB((s) => {
    const next = structuredClone(s);
    let node = next;
    const parts = path.split(".");
    for (let i = 0; i < parts.length - 1; i++) node = node[parts[i]] = { ...(node[parts[i]] || {}) };
    node[parts[parts.length - 1]] = value;
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await http.put("/branding", b);
      qc.invalidateQueries({ queryKey: ["public-branding"] });
      qc.invalidateQueries({ queryKey: ["branding-admin"] });
      toast.success("Branding saved — applied across the platform");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const previewPlans = useMemo(() => plansData?.plans || [], [plansData]);
  if (!b) return <div className="h-40 animate-pulse rounded-xl bg-white/[0.03]" />;

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]" data-testid="branding-editor">
      {/* Left: form */}
      <div className="space-y-5">
        <Group title="Brand identity" desc="Name, logo, favicon & accent color used everywhere.">
          <TextField label="Brand / platform name" value={b.brand.name} onChange={(v) => set("brand.name", v)} testId="brand-name" placeholder="e.g. Tracify" />
          <TextField label="Tagline" value={b.brand.tagline} onChange={(v) => set("brand.tagline", v)} testId="brand-tagline" placeholder="Affiliate platform" />
          <AssetUpload label="Logo" kind="logo" value={b.brand.logo_id} onChange={(v) => set("brand.logo_id", v)} shape="wide" hint="PNG/SVG, transparent" testId="brand-logo" />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <AssetUpload label="Favicon" kind="favicon" value={b.brand.favicon_id} onChange={(v) => set("brand.favicon_id", v)} shape="favicon" hint="512×512 PNG" testId="brand-favicon" />
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Accent color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={b.brand.accent_color} onChange={(e) => set("brand.accent_color", e.target.value)} data-testid="brand-accent" className="h-9 w-12 cursor-pointer rounded-md border border-white/15 bg-transparent" />
                <TextField value={b.brand.accent_color} onChange={(v) => set("brand.accent_color", v)} testId="brand-accent-hex" className="flex-1" />
              </div>
            </div>
          </div>
        </Group>

        <Group title="Landing content" desc="Hero copy, image and call-to-action labels.">
          <TextField label="Eyebrow" value={b.landing.eyebrow} onChange={(v) => set("landing.eyebrow", v)} testId="land-eyebrow" />
          <TextField label="Headline" value={b.landing.headline} onChange={(v) => set("landing.headline", v)} testId="land-headline" />
          <TextareaField label="Subheadline" value={b.landing.subheadline} onChange={(v) => set("landing.subheadline", v)} testId="land-subheadline" />
          <AssetUpload label="Hero image (optional)" kind="cover" value={b.landing.hero_image_id} onChange={(v) => set("landing.hero_image_id", v)} shape="wide" hint="Wide, ~1600×900" testId="land-hero" />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Primary button (Login)" value={b.landing.cta_primary_label} onChange={(v) => set("landing.cta_primary_label", v)} testId="land-cta1" />
            <TextField label="Secondary button (Join)" value={b.landing.cta_secondary_label} onChange={(v) => set("landing.cta_secondary_label", v)} testId="land-cta2" />
          </div>
          <SwitchField label="Show pricing section" hint="Pulls active plans from Billing → Plans." checked={b.landing.show_pricing !== false} onChange={(v) => set("landing.show_pricing", v)} testId="land-show-pricing" />
        </Group>

        <Group title="Contact / support" desc="Shown in the Join card & landing. Empty fields are hidden.">
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Support name" value={b.contact.support_name} onChange={(v) => set("contact.support_name", v)} testId="contact-name" />
            <TextField label="Title / role" value={b.contact.support_title} onChange={(v) => set("contact.support_title", v)} testId="contact-title" />
          </div>
          <AssetUpload label="Support photo" kind="avatar" value={b.contact.support_photo_id} onChange={(v) => set("contact.support_photo_id", v)} shape="circle" testId="contact-photo" />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Telegram" value={b.contact.telegram} onChange={(v) => set("contact.telegram", v)} testId="contact-telegram" placeholder="@handle or link" />
            <TextField label="Email" value={b.contact.email} onChange={(v) => set("contact.email", v)} testId="contact-email" />
            <TextField label="Instagram" value={b.contact.instagram} onChange={(v) => set("contact.instagram", v)} testId="contact-instagram" />
            <TextField label="Discord / support link" value={b.contact.discord} onChange={(v) => set("contact.discord", v)} testId="contact-discord" />
          </div>
        </Group>

        <Group title="SEO & footer" desc="Meta tags for public pages.">
          <TextField label="SEO title" value={b.seo.title} onChange={(v) => set("seo.title", v)} testId="seo-title" />
          <TextareaField label="SEO description" value={b.seo.description} onChange={(v) => set("seo.description", v)} testId="seo-desc" rows={2} />
          <AssetUpload label="OpenGraph image" kind="cover" value={b.seo.og_image_id} onChange={(v) => set("seo.og_image_id", v)} shape="wide" hint="1200×630" testId="seo-og" />
          <TextField label="Footer text" value={b.footer.text} onChange={(v) => set("footer.text", v)} testId="footer-text" />
        </Group>

        <Group title="Access" desc="How the Join / Get access button behaves.">
          <SwitchField label="Public self-registration" hint="On: Join opens sign-in/registration. Off: Join opens the contact card." checked={!!b.public_registration} onChange={(v) => set("public_registration", v)} testId="public-reg" />
        </Group>

        <Button onClick={save} disabled={saving} data-testid="branding-save" className="bg-white text-black hover:bg-white/90">{saving ? "Saving…" : "Save branding"}</Button>
      </div>

      {/* Right: sticky live preview */}
      <div className="lg:sticky lg:top-6 lg:h-[calc(100vh-8rem)]">
        <div className="flex items-center gap-2 pb-2 text-xs uppercase tracking-wider text-muted-foreground"><Eye className="h-3.5 w-3.5" /> Live preview</div>
        <div className="h-[calc(100%-1.75rem)] overflow-hidden rounded-2xl border border-white/[0.1]">
          <div className="h-full overflow-y-auto no-scrollbar" data-testid="branding-preview">
            <LandingView branding={b} plans={b.landing.show_pricing !== false ? previewPlans : []} onLogin={() => {}} onJoin={() => {}} preview />
          </div>
        </div>
      </div>
    </div>
  );
}
